import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FilterParams } from '../../models/filter-params';
import { FilterHeaderComponent } from '../filter-header/range-date-filter.component';
import { format } from 'date-fns';

type TypeDateFilters = 'eq' | 'gt' | 'gte' | 'lt' | 'lte';

type FilterDateOptions = {
  [P in TypeDateFilters]: {
    label: string;
    type: P;
  };
};

const filters: FilterDateOptions = {
  eq: {
    label: 'Igual a',
    type: 'eq',
  },
  gt: {
    label: 'Mayor que',
    type: 'gt',
  },
  gte: {
    label: 'Mayor o igual que',
    type: 'gte',
  },
  lt: {
    label: 'Menor que',
    type: 'lt',
  },
  lte: {
    label: 'Menor o igual que',
    type: 'lte',
  },
};

/*
This class represents a date filter component in Angular. It provides a user interface for selecting a date and a filter option, and emits an event with the selected filter parameters when the filter is applied.

Usage:
<edt-date-filter
  [propFilter]="'date'"
  [title]="'Date Filter'"
  [filterOptions]="{ eq: { label: 'Equal to', type: 'eq' }, gt: { label: 'Greater than', type: 'gt' } }"
  (filterChanged)="handleFilterChanged($event)"
></edt-date-filter>

Methods:
- close(): Closes the date picker if it is not already opened.
- clear(): Clears the selected date and emits the filter parameters with a null value.
- changeSelectionFilter(): Applies the filter when the filter option is changed, if a date is selected.
- changeDate(event: MatDatepickerInputEvent<Date>): Applies the filter when the date is changed, if a date is selected.
- applyFilter(): Emits the filter parameters with the selected date and filter option.
- toggleOpen(open: boolean): Toggles the open state of the date picker.

Fields:
- propFilter: The name of the filter parameter to be used in the emitted filter parameters.
- title: The title of the date filter component.
- filterOptions: The available filter options for the date filter.
- filterChanged: An event emitter for the filter changed event.
- datePicker: A reference to the MatDatepicker component used for selecting dates.
- isOpen: A boolean indicating whether the date picker is open or closed.
- selectedDate: The currently selected date.
- selectedFilter: The currently selected filter option.
- keys: An array of the keys of the filter options.
*/

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'edt-date-filter',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    OverlayModule,
    FilterHeaderComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
  ],
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateFilterComponent {
  /**
   * The name of the filter parameter to be used in the emitted filter parameters.
   */
  @Input({ required: true }) propFilter = 'filter';

  /**
   * The title of the date filter component.
   */
  @Input({ required: true }) title = 'Title';

  /**
   * The available filter options for the date filter.
   */
  @Input() filterOptions: Partial<FilterDateOptions> = filters;

  @Input() separator = '__';

  /**
   * An event emitter for the filter changed event.
   */
  @Output() filterChanged = new EventEmitter<FilterParams>();

  /**
   * A reference to the MatDatepicker component used for selecting dates.
   */
  @ViewChild(MatDatepicker) datePicker!: MatDatepicker<Date>;

  /**
   * A boolean indicating whether the date picker is open or closed.
   */
  isOpen = false;

  /**
   * The currently selected date.
   */
  selectedDate: Date | null = null;

  /**
   * The currently selected filter option.
   */
  selectedFilter = this.keys[0];

  /**
   * Closes the date picker if it is not already opened.
   */
  close() {
    if (!this.datePicker.opened) this.isOpen = false;
  }

  /**
   * Clears the selected date and emits the filter parameters with a null value.
   */
  clear() {
    this.close();
    this.selectedDate = null;
    this._emitSelectionChanged();
  }

  /**
   * Applies the filter when the filter option is changed, if a date is selected.
   */
  changeSelectionFilter() {
    if (this.selectedDate !== null) {
      this.applyFilter();
    }
  }

  /**
   * Applies the filter when the date is changed, if a date is selected.
   * @param event The MatDatepickerInputEvent containing the new date value.
   */
  changeDate(event: MatDatepickerInputEvent<Date>) {
    if (event.value !== null) {
      this.applyFilter();
    }
  }

  /**
   * Emits the filter parameters with the selected date and filter option.
   */
  applyFilter(): void {
    this._emitSelectionChanged();
  }

  /**
   * Toggles the open state of the date picker.
   * @param open A boolean indicating whether the date picker should be opened or closed.
   */
  toggleOpen(open: boolean) {
    this.isOpen = open;
  }

  /**
   * Returns an array of the keys of the filter options.
   */
  get keys() {
    return Object.keys(this.filterOptions).map((key) => key as TypeDateFilters);
  }

  /**
   * Emits the filter parameters with the selected date and filter option.
   * @private
   */
  private _emitSelectionChanged() {
    const filterParams: FilterParams = {};
    if (this.selectedDate)
      filterParams[
        `${this.propFilter}${this.separator}${this.selectedFilter}`
      ] = format(this.selectedDate, 'yyyy-MM-dd');
    this.filterChanged.emit(filterParams);
  }
}
